import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { Video, Calendar, Clock, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DailyVideoPlayer } from '@/components/video/daily-video-player';

interface LiveSession {
  id: string;
  title: string;
  description: string;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  daily_room_name: string | null;
  daily_room_url: string | null;
  teacher_id: string;
  courses: {
    title: string;
    teacher_id: string;
  };
}

export default function LiveSessionDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [session, setSession] = useState<LiveSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [videoToken, setVideoToken] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (params.id) {
      fetchSession();
      checkEnrollment();
    }
  }, [params.id, profile]);

  const fetchSession = async () => {
    const { data, error } = await supabase
      .from('live_sessions')
      .select(`
        *,
        courses:course_id(title, teacher_id)
      `)
      .eq('id', params.id)
      .single();

    if (data) {
      if (data.daily_room_url) {
        console.log('Fetched room URL:', data.daily_room_url);
        console.log('Room URL bytes:', [...data.daily_room_url].map((c) => c.charCodeAt(0)));
        data.daily_room_url = data.daily_room_url.trim().replace(/[\x00-\x1F\x7F]/g, '');
      }
      if (data.daily_room_name) {
        console.log('Fetched room name:', data.daily_room_name);
        data.daily_room_name = data.daily_room_name.trim().replace(/[\x00-\x1F\x7F]/g, '');
      }
      setSession(data);
    }
    setLoading(false);
  };

  const checkEnrollment = async () => {
    if (!profile?.id || !params.id) return;

    const { data: sessionData } = await supabase
      .from('live_sessions')
      .select('course_id, teacher_id')
      .eq('id', params.id)
      .single();

    if (!sessionData) return;

    if (sessionData.teacher_id === profile.id) {
      setIsEnrolled(true);
      return;
    }

    const { data } = await supabase
      .from('enrollments')
      .select('id')
      .eq('student_id', profile.id)
      .eq('course_id', sessionData.course_id)
      .maybeSingle();

    setIsEnrolled(!!data);
  };

  const handleStartSession = async () => {
    if (!session || session.teacher_id !== profile?.id) return;

    setJoining(true);

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();

      if (!authSession) {
        throw new Error('Non autenticato');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-room`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          sessionId: session.id,
          sessionTitle: session.title,
        }),
      });

      const data = await response.json();
      console.log('Create room response:', data);

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.roomName || !data.roomUrl) {
        throw new Error('Room data incomplete');
      }

      const cleanRoomUrl = data.roomUrl.trim();
      const cleanRoomName = data.roomName.trim();

      console.log('Clean room URL:', cleanRoomUrl);
      console.log('Clean room name:', cleanRoomName);

      await supabase
        .from('live_sessions')
        .update({
          status: 'live',
          daily_room_name: cleanRoomName,
          daily_room_url: cleanRoomUrl,
        })
        .eq('id', session.id);

      setSession({
        ...session,
        status: 'live',
        daily_room_name: cleanRoomName,
        daily_room_url: cleanRoomUrl,
      });

      await joinRoom(cleanRoomName);
    } catch (error: any) {
      console.error('Error starting session:', error);
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile avviare la sessione',
        variant: 'destructive',
      });
      setJoining(false);
    }
  };

  const handleEndSession = async () => {
    if (!session || session.teacher_id !== profile?.id || !session.daily_room_name) return;

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();

      if (!authSession) {
        throw new Error('Non autenticato');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/delete-room`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          roomName: session.daily_room_name,
        }),
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      await supabase
        .from('live_sessions')
        .update({
          status: 'ended',
          daily_room_name: null,
          daily_room_url: null,
        })
        .eq('id', session.id);

      toast({
        title: 'Successo',
        description: 'Sessione terminata',
      });

      navigate('/dashboard/live');
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile terminare la sessione',
        variant: 'destructive',
      });
    }
  };

  const joinRoom = async (roomName?: string) => {
    if (!session) return;

    setJoining(true);

    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();

      if (!authSession) {
        throw new Error('Non autenticato');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const requestBody = {
        roomName: roomName || session.daily_room_name,
        sessionId: session.id,
        userId: profile?.id,
        userName: profile?.full_name || profile?.email || 'User',
        userRole: profile?.role,
      };

      console.log('Getting token with request:', requestBody);

      const response = await fetch(`${supabaseUrl}/functions/v1/get-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Get token response status:', response.status);

      const data = await response.json();
      console.log('Get token response data:', data);

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.token) {
        throw new Error('Token not received');
      }

      console.log('Setting video token, showing video player');
      setVideoToken(data.token);
      setShowVideo(true);

      toast({
        title: 'Connesso',
        description: 'Sei entrato nella live session',
      });
    } catch (error: any) {
      console.error('Error joining room:', error);
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile partecipare alla sessione',
        variant: 'destructive',
      });
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveRoom = () => {
    setShowVideo(false);
    setVideoToken(null);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge className="bg-red-600">In diretta</Badge>;
      case 'scheduled':
        return <Badge variant="secondary">Programmata</Badge>;
      case 'ended':
        return <Badge variant="outline">Terminata</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const isTeacher = session?.teacher_id === profile?.id;
  const canJoin = isEnrolled && (session?.status === 'live' || isTeacher);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  if (!session) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Sessione non trovata</AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/live">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                {session.title}
              </h1>
              {getStatusBadge(session.status)}
            </div>
            <p className="text-sm text-gray-600">{session.courses?.title}</p>
          </div>
        </div>

        {!isEnrolled && !isTeacher && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Devi essere iscritto al corso per partecipare a questa live session
            </AlertDescription>
          </Alert>
        )}

        {session.status === 'live' && canJoin && showVideo && videoToken && session.daily_room_url && (
          <Card>
            <CardHeader>
              <CardTitle>Video Live</CardTitle>
              <CardDescription>
                La sessione è in corso
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DailyVideoPlayer
                roomUrl={session.daily_room_url}
                token={videoToken}
                userName={profile?.full_name || profile?.email || 'User'}
                onLeave={handleLeaveRoom}
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Dettagli sessione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {session.description && (
              <div>
                <h3 className="font-semibold mb-2">Descrizione</h3>
                <p className="text-gray-700">{session.description}</p>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center text-gray-700">
                <Calendar className="w-5 h-5 mr-3 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Data e ora</p>
                  <p className="font-medium">
                    {new Date(session.scheduled_at).toLocaleString('it-IT')}
                  </p>
                </div>
              </div>

              <div className="flex items-center text-gray-700">
                <Clock className="w-5 h-5 mr-3 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Durata</p>
                  <p className="font-medium">{session.duration_minutes} minuti</p>
                </div>
              </div>
            </div>

            <div className="pt-4 flex gap-3">
              {isTeacher && session.status === 'scheduled' && (
                <Button
                  onClick={handleStartSession}
                  disabled={joining}
                  className="flex-1"
                >
                  {joining && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Video className="w-4 h-4 mr-2" />
                  Avvia sessione
                </Button>
              )}

              {session.status === 'live' && canJoin && !showVideo && (
                <Button
                  onClick={() => joinRoom()}
                  disabled={joining}
                  className="flex-1"
                >
                  {joining && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Video className="w-4 h-4 mr-2" />
                  Partecipa
                </Button>
              )}

              {session.status === 'live' && isTeacher && (
                <Button
                  onClick={handleEndSession}
                  variant="destructive"
                  className="flex-1"
                >
                  Termina sessione
                </Button>
              )}

              {session.status === 'ended' && (
                <Alert>
                  <AlertDescription>
                    Questa sessione è terminata
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
